/**
 * Site status — single source of truth (API Title Case values).
 */

export const SITE_STATUS = {
  ACTIVE: "Active",
  DEACTIVATED: "Deactivated",
} as const;

/** @deprecated Use SITE_STATUS — alias for StudyManagement re-exports. */
export const SITE_STATUSES = SITE_STATUS;

export const SITE_STATUS_VALUES = Object.values(SITE_STATUS);

export type SiteStatus = (typeof SITE_STATUS)[keyof typeof SITE_STATUS];

const canonicalize = (value: string): string =>
  value.toLowerCase().replace(/[\s/\-_]+/g, "");

const STATUS_BY_KEY: Record<string, SiteStatus> = Object.values(
  SITE_STATUS,
).reduce(
  (acc, value) => {
    acc[canonicalize(value)] = value;
    return acc;
  },
  {} as Record<string, SiteStatus>,
);

export const tryNormalizeSiteStatus = (status: string): SiteStatus | null => {
  if (!status?.trim()) {
    return null;
  }
  if (SITE_STATUS_VALUES.includes(status as SiteStatus)) {
    return status as SiteStatus;
  }
  return STATUS_BY_KEY[canonicalize(status)] ?? null;
};

/** Normalize to SiteStatus; unknown values default to Active. */
export const normalizeSiteStatus = (status: string): SiteStatus =>
  tryNormalizeSiteStatus(status) ?? SITE_STATUS.ACTIVE;

/** Strict compare: unknown status never matches (no Active default). */
export const isSiteStatus = (status: string, target: SiteStatus): boolean =>
  tryNormalizeSiteStatus(status) === target;

export const isAnySiteStatus = (
  status: string,
  ...targets: SiteStatus[]
): boolean => {
  const normalized = tryNormalizeSiteStatus(status);
  return normalized !== null && targets.includes(normalized);
};
